import { Sensor, SensorInfo, SensorSource, UserGroup, VirtualSensorFunction, VirtualSensorConfig, VirtualSensorParameter, VirtualSensorReference, LastSensorReading, SensorCalibrationHistoryInfo, SensorCalibrationHistory } from "@models";
import { GpsInfo } from "@models/Map/gpsInfo.model";
import moment from 'moment';
import { Authorization } from "./authorization";

export class SensorMapping {
  
  public auth = new Authorization();
  
  constructor() {}

  mapSensorInfos2Sensors(sensorInfos: SensorInfo[], allGpsInfos: GpsInfo[]): Sensor[] {
    var sensors: Sensor[] = [];
    sensorInfos.forEach(x => {
      var sensor = this.mapSensorInfo2Sensor(x, allGpsInfos);
      sensors.push(sensor);
    });
    return sensors;
  }
  
  
  mapSensorInfo2Sensor(sensorInfo: SensorInfo, allGpsInfos: GpsInfo[]): Sensor {
    var sensor: Sensor = {
      id: sensorInfo.id,
      uuid: sensorInfo.uuid,
      name: sensorInfo.name,
      type: sensorInfo.type,
      referenceInfo: sensorInfo.referenceInfo,
      source: this.getSensorSourceAtIndex(Number(sensorInfo.source)),
      sensorReadingTypes: sensorInfo.sensorReadingTypes,
      sensorReadingTypeNames: (sensorInfo.sensorReadingTypes != undefined) ? sensorInfo.sensorReadingTypes.map(x => x.type).join(', ').toLowerCase() : '',
      modifiedDate: (sensorInfo.modifiedDate != undefined) ? moment.utc(sensorInfo.modifiedDate).local().toDate() : moment("1984-01-01").toDate(),
      checked: false,
      sensorCalibrationHistory: (sensorInfo.sensorCalibrationHistory != undefined) ? this.mapCalibrationHistoryInfos2CalibrationHistory(sensorInfo.sensorCalibrationHistory) : undefined,
      userName: sensorInfo.userName,
      userGroupId: sensorInfo.userGroupId,
      displayedOwner: undefined,
      notes: sensorInfo.notes,
      virtualSensor: sensorInfo.virtualSensorInfo,
      gpsLocation: this.getGpsInfo(sensorInfo.gpsLocation, allGpsInfos),
      lastReading: this.createLastSensorReading(sensorInfo.id),
      searchResults: sensorInfo.searchResults,
      ignore: sensorInfo.ignore || false,
      ignoreUntil: sensorInfo.ignoreUntil || undefined
    }

    return sensor;
  }
  
  
  createLastSensorReading(id: number): LastSensorReading {
    var lastReading: LastSensorReading = {
      sensorId: id,
      isLoaded: false,
      date: undefined,
      displayColor: "gray",
      waitingForLoad: false
    }
    return lastReading;
  }


  mapSensors2SensorInfos(sensors: Sensor[]): SensorInfo[] {
    var sensorInfos: SensorInfo[] = [];
    sensors.forEach(x => {
      var sensorInfo = this.mapSensor2SensorInfo(x);
      sensorInfos.push(sensorInfo);
    });
    return sensorInfos;
  }
  
  
  mapSensor2SensorInfo(sensor: Sensor): SensorInfo {
    var sensorInfo: SensorInfo = {
      id: sensor.id,
      uuid: sensor.uuid,
      name: sensor.name || '',
      type: sensor.type || '',
      referenceInfo: sensor.referenceInfo || '',
      source: sensor.source || SensorSource.Physical,
      sensorReadingTypes: sensor.sensorReadingTypes,
      modifiedDate: sensor.modifiedDate,
      sensorCalibrationHistory: (sensor.sensorCalibrationHistory != undefined) ? this.mapCalibrationHistories2CalibrationHistoryInfos(sensor.sensorCalibrationHistory) : undefined,
      userName: sensor.userName,
      userGroupId: sensor.userGroupId,
      notes: sensor.notes || '',
      virtualSensorInfo: sensor.virtualSensor,
      gpsLocation: sensor.gpsLocation || this.newGpsInfo(),
      searchResults: sensor.searchResults,
      ignore: sensor.ignore || false,
      ignoreUntil: sensor.ignoreUntil || undefined
    }
    return sensorInfo;
  }
  
  
  mapCalibrationHistoryInfos2CalibrationHistory(calibrationInfos: SensorCalibrationHistoryInfo[]): SensorCalibrationHistory[] {
    var calibrations: SensorCalibrationHistory[] = [];
    calibrationInfos.forEach(x => {
      var calibration = this.mapCalibrationHistoryInfo2CalibrationHistory(x);
      calibrations.push(calibration);
    });
    return calibrations;
  }
  
  
  mapCalibrationHistoryInfo2CalibrationHistory(calibrationInfo: SensorCalibrationHistoryInfo): SensorCalibrationHistory {
    var calibration: SensorCalibrationHistory = {
      id: (calibrationInfo.id != undefined) ? calibrationInfo.id : 0,
      sensorId: (calibrationInfo.sensorId != undefined) ? calibrationInfo.sensorId : 0,
      sensorName: (calibrationInfo.sensorName != undefined) ? calibrationInfo.sensorName : '',
      sensorReadingTypeId: (calibrationInfo.sensorReadingTypeId != undefined) ? calibrationInfo.sensorReadingTypeId : 0,
      sensorReadingTypeName: '',
      equationId: (calibrationInfo.templateId != undefined) ? calibrationInfo.templateId : -1,
      parameters: (calibrationInfo.parameters != undefined) ? JSON.parse(calibrationInfo.parameters) : [1, 0, 0, 0],
      checked: true,
      showChartRawValues: true,
      showChartCalValues: true,
      isActive: (calibrationInfo.isActive == 1) ? true : false,
      startDate: calibrationInfo.startDate,
      modifiedDate: calibrationInfo.modifiedDate,
      modifiedByUserName: calibrationInfo.modifiedByUserName,
      notes: calibrationInfo.notes
    }
    return calibration;
  }


  mapCalibrationHistories2CalibrationHistoryInfos(calibrations: SensorCalibrationHistory[]): SensorCalibrationHistoryInfo[] {
    var calibrationInfos: SensorCalibrationHistoryInfo[] = [];
    calibrations.forEach(x => {
      var calibrationInfo = this.mapCalibrationHistory2CalibrationHistoryInfo(x);
      calibrationInfos.push(calibrationInfo);
    });
    return calibrationInfos;
  }


  mapCalibrationHistory2CalibrationHistoryInfo(calibration: SensorCalibrationHistory): SensorCalibrationHistoryInfo {
    var calibrationInfo: SensorCalibrationHistoryInfo = {
      id: calibration.id,
      sensorId: calibration.sensorId,
      sensorReadingTypeId: calibration.sensorReadingTypeId,
      templateId: calibration.equationId,
      parameters: JSON.stringify(calibration.parameters),
      isActive: (calibration.isActive) ? 1 : 0,
      sensorName: '',
      startDate: calibration.startDate || moment("1984-01-01").toDate(),
      modifiedDate: calibration.modifiedDate || moment("1984-01-01").toDate(),
      modifiedByUserId: 0,
      modifiedByUserName: calibration.modifiedByUserName || '',
      notes: calibration.notes || ''
    }
    return calibrationInfo;
  }


  getSensorSourceAtIndex(index: number): SensorSource {
    const enumString = (index >= 0) ? Object.values(SensorSource)[index] : '';
    switch (enumString) {
    case 'Physical':
      return SensorSource.Physical;
    case 'Virtual':
      return SensorSource.Virtual;
    case 'Internal':
      return SensorSource.Internal;
    default:
      return SensorSource.Physical;
    }
  }

  
  getGpsInfo(gpsInfo: GpsInfo, allGpsInfos: GpsInfo[]): GpsInfo {
    if (gpsInfo == undefined)
      return this.newGpsInfo();

    gpsInfo = allGpsInfos.find(x => x.id == gpsInfo.id);

    return (gpsInfo != undefined) ? gpsInfo : this.newGpsInfo();
  }


  newGpsInfo(): GpsInfo {
    var newGpsInfo: GpsInfo = {
      id: 0,
      gpsLong: 0,
      gpsLat: 0,
      gpsAlt: 0,
      locationDescription: ''
    }
    return newGpsInfo;
  }



}